import { useMemo } from 'react';

import { useIntl } from '@futrli/localisation';

import { buildFilterFieldConfigs } from '../../../../utils/list-filters/list-filter-utils';
import { CAS_MESSAGES } from './constants';
import { CASFilters } from './types';
import { FilterColumnDefinition } from '../../../../utils/list-filters/list-filter-types';
import { FilterFieldConfig } from '../../../../ui/filters-sidebar/types';
import { formatDateForInput } from './helpers';

/**
 * Build filter column definitions for the Customers and Suppliers sidebar.
 * @param contactOptions - The available contact options for the contact filter field
 * @param currencySymbol - The currency symbol to display in range filter labels
 * @returns An array of filter column definitions for the Customers and Suppliers list
 */
const buildCASFilterColumns = (
  contactOptions: { value: string; text: string }[],
  currencySymbol: string
): FilterColumnDefinition[] => [
  {
    key: 'contacts',
    type: 'textMatch',
    labelId: CAS_MESSAGES.contact.id!,
    labelDefaultMessage: CAS_MESSAGES.contact.defaultMessage as string,
    options: contactOptions
  },
  {
    key: 'totalDue',
    type: 'range',
    labelId: 'predict.column_total_due',
    labelDefaultMessage: 'Total due',
    currencySymbol
  },
  {
    key: 'totalOverdue',
    type: 'range',
    labelId: 'predict.column_total_overdue',
    labelDefaultMessage: 'Total overdue',
    currencySymbol
  },
  {
    key: 'totalInvoiced',
    type: 'range',
    labelId: 'predict.column_total_invoiced',
    labelDefaultMessage: 'Total invoiced',
    currencySymbol
  },
  {
    key: 'invoicesDue',
    type: 'range',
    labelId: 'predict.column_invoices_due',
    labelDefaultMessage: 'Invoices due'
  },
  {
    key: 'averageSale',
    type: 'range',
    labelId: 'predict.column_average_sale',
    labelDefaultMessage: 'Average sale',
    currencySymbol
  },
  { key: 'daysToPay', type: 'range', labelId: 'predict.days_to_pay', labelDefaultMessage: 'Days to pay' },
  { key: 'dependence', type: 'range', labelId: 'predict.column_dependence', labelDefaultMessage: 'Dependence' },
  {
    key: 'lastActivity',
    type: 'dateRange',
    labelId: 'predict.column_last_activity',
    labelDefaultMessage: 'Last activity',
    formatDateForInput
  }
];

interface UseFilterConfigParams {
  contactOptions: { value: string; text: string }[];
  draftFilters: CASFilters;
  activeFilterKeys: Set<string>;
  currencySymbol: string;
}

/**
 * Custom hook to build filter configuration for the filters sidebar, based on current filter state and options
 * @param contactOptions - The available contact options for the contact filter field
 * @param draftFilters - The current state of the draft filters being edited in the sidebar
 * @param activeFilterKeys - A set of keys representing which filters are currently active
 * @param currencySymbol - The currency symbol to display in range filter labels
 * @returns An array of filter field configurations for the sidebar
 */
export const useFilterConfig = ({
  contactOptions,
  draftFilters,
  activeFilterKeys,
  currencySymbol
}: UseFilterConfigParams): FilterFieldConfig[] => {
  const intl = useIntl();

  return useMemo(() => {
    const columns = buildCASFilterColumns(contactOptions, currencySymbol);
    const labels = {
      showAllLabel: intl.formatMessage({ id: 'predict.show_all', defaultMessage: 'Show all' }),
      filterByRangeLabel: intl.formatMessage({ id: 'predict.filter_by_range', defaultMessage: 'Filter by range' }),
      fromLabel: intl.formatMessage({ id: 'common.from_number', defaultMessage: 'From' }),
      toLabel: intl.formatMessage({ id: 'common.to_number', defaultMessage: 'To' })
    };

    // Type assertions bridge CASFilters ↔ the generic Record<string, unknown>
    // expected by buildFilterFieldConfigs (generic utility, not CAS-aware).
    return buildFilterFieldConfigs(
      columns,
      (draftFilters as unknown) as Record<string, unknown>,
      activeFilterKeys,
      intl,
      labels
    ) as FilterFieldConfig[];
  }, [intl, contactOptions, draftFilters, activeFilterKeys, currencySymbol]);
};
